import { useEffect, useRef, useState } from "react";
import { useRealtimeKitSelector } from "@cloudflare/realtimekit-react";

export function useMicrophoneVolume() {
  const [volume, setVolume] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Get self from Cloudflare selector
  const self = useRealtimeKitSelector((m) => m.self);

  useEffect(() => {
    if (!self?.audioTrack) {
      setVolume(0);
      setIsSpeaking(false);
      return;
    }

    const setupAudioAnalysis = async () => {
      try {
        // Create audio context
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        // Create analyser node
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;
        
        // Create media stream source from the audio track
        const stream = new MediaStream([self.audioTrack]);
        const source = audioContextRef.current.createMediaStreamSource(stream);
        
        // Connect source to analyser
        source.connect(analyserRef.current);
        
        // Create data array for frequency analysis
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);
        
        // Start analysis loop
        const analyzeAudio = () => {
          if (!analyserRef.current || !dataArrayRef.current) return;
          
          analyserRef.current.getByteFrequencyData(dataArrayRef.current);
          
          // Calculate average volume
          let sum = 0;
          for (let i = 0; i < dataArrayRef.current.length; i++) {
            sum += dataArrayRef.current[i];
          }
          const average = sum / dataArrayRef.current.length;
          const normalizedVolume = average / 255; // Normalize to 0-1
          
          setVolume(normalizedVolume);
          
          // Determine if speaking (threshold can be adjusted)
          const speakingThreshold = 0.1;
          setIsSpeaking(normalizedVolume > speakingThreshold);
          
          animationFrameRef.current = requestAnimationFrame(analyzeAudio);
        };
        
        analyzeAudio();
      } catch (error) {
        console.error("Error setting up audio analysis:", error);
      }
    };

    setupAudioAnalysis();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [self?.audioTrack]);

  return { volume, isSpeaking };
} 